import html2pdf from "html2pdf.js";
import jsPDF from "jspdf";
import { convertMarkdownToHTML } from "./markdownUtils";
import { renderBlocksToHTML, convertContentToBlocks } from "./documentUtils";

// Enhanced PDF generation with professional formatting using HTML2PDF
export const generatePDFFromHTML = async (
  element: HTMLElement,
  filename: string,
  options?: any
) => {
  const defaultOptions = {
    margin: [0.75, 0.75, 0.75, 0.75],
    filename: filename,
    image: {
      type: "jpeg",
      quality: 0.98,
      crossOrigin: "anonymous",
    },
    html2canvas: {
      scale: 3, // Increased scale for better quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      removeContainer: true,
      imageTimeout: 30000, // Increased timeout for images
      logging: false,
      letterRendering: true, // Better text rendering
      foreignObjectRendering: true, // Better SVG/complex element rendering
    },
    jsPDF: {
      unit: "in",
      format: "a4",
      orientation: "portrait",
      compress: true,
      precision: 16, // Higher precision for better layout
    },
    pagebreak: {
      mode: ["avoid-all", "css", "legacy"],
      before: ".page-break-before",
      after: ".page-break-after",
      avoid: ["tr", "img"], // Avoid breaking images and table rows
    },
  };

  const finalOptions = { ...defaultOptions, ...options };
  return html2pdf().set(finalOptions).from(element).save();
};

// Enhanced PDF generation that matches preview exactly
export const generatePDFFromPreviewElement = async (
  element: HTMLElement,
  filename: string,
  documentTheme?: any
) => {
  try {
    console.log("🎯 Generating PDF with exact preview fidelity...");

    // Clone the element to avoid modifying the original
    const clonedElement = element.cloneNode(true) as HTMLElement;

    // Apply additional styling to ensure perfect rendering
    const style = document.createElement("style");
    style.textContent = `
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        img {
          max-width: 100% !important;
          height: auto !important;
          page-break-inside: avoid !important;
        }

        .page-break-before {
          page-break-before: always !important;
        }

        .page-break-after {
          page-break-after: always !important;
        }

        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid !important;
          page-break-inside: avoid !important;
        }
      }
    `;
    document.head.appendChild(style);

    const options = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: filename,
      image: {
        type: "jpeg",
        quality: 1.0,
        crossOrigin: "anonymous",
      },
      html2canvas: {
        scale: 4, // Very high scale for crisp output
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        removeContainer: true,
        imageTimeout: 45000,
        logging: false,
        letterRendering: true,
        foreignObjectRendering: true,
        onclone: (clonedDoc: Document) => {
          // Ensure all images are loaded in the cloned document
          const images = clonedDoc.querySelectorAll("img");
          images.forEach((img) => {
            img.style.maxWidth = "100%";
            img.style.height = "auto";
          });
        },
      },
      jsPDF: {
        unit: "in",
        format: "a4",
        orientation: "portrait",
        compress: true,
        precision: 16,
      },
      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
        before: ".page-break-before",
        after: ".page-break-after",
        avoid: ["tr", "img", "h1", "h2", "h3"],
      },
    };

    const result = await html2pdf().set(options).from(clonedElement).save();

    // Clean up
    document.head.removeChild(style);

    return result;
  } catch (error) {
    console.error("Enhanced PDF generation failed:", error);
    // Fallback to standard generation
    return generatePDFFromHTML(element, filename);
  }
};

// EMERGENCY FIX: Direct jsPDF generation that WILL work
export const generatePDFFromPreview = async (
  documentData: any,
  documentTheme: any,
  filename: string
) => {
  try {
    console.log("🎯 PDF generation starting with content verification...");
    console.log("Document data:", documentData);
    console.log("Document theme:", documentTheme);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin + 10;

    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
        : { r: 0, g: 0, b: 0 };
    };

    const primaryColor = documentTheme?.headingColor || "#1a365d";
    const primaryRgb = hexToRgb(primaryColor);
    const secondaryColor = documentTheme?.subheadingColor || "#374151";
    const secondaryRgb = hexToRgb(secondaryColor);
    const textColor = documentTheme?.textColor || "#000000";
    const textRgb = hexToRgb(textColor);

    // Store logo base64 for reuse across pages
    let logoBase64 = null;

    // Prepare logo if available (for page headers, not title page)
    if (documentTheme?.logo) {
      try {
        console.log("🖼️ Processing logo:", documentTheme.logo);

        // Handle different logo formats
        if (documentTheme.logo.startsWith('data:image/')) {
          // Already base64
          logoBase64 = documentTheme.logo;
        } else {
          // Fetch from URL
          const response = await fetch(documentTheme.logo);
          if (!response.ok) {
            throw new Error(`Failed to fetch logo: ${response.status}`);
          }
          const blob = await response.blob();
          logoBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }
        console.log("✅ Logo prepared successfully");
      } catch (error) {
        console.warn("⚠️ Could not prepare logo for PDF:", error);
      }
    }

    // Process sections directly (no title page)
    const sections =
      documentData.editableSections || documentData.sections || [];

    console.log("📄 Processing sections:", sections.length);

    if (sections.length === 0) {
      console.warn("⚠️ No sections found in document data");
      doc.setFontSize(16);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text("No content available to display.", margin, yPosition);
    } else {
      sections.forEach((section: any, index: number) => {
        console.log(`📝 Processing section ${index + 1}:`, section.title || 'Untitled');

        // Add new page only for subsequent sections (not the first one)
        if (index > 0) {
          doc.addPage();
          yPosition = margin + 10;
        }

        // Add small logo to page header if available
        if (logoBase64) {
          try {
            // Add small logo to top right
            const logoWidth = 20;
            const logoHeight = 12;
            doc.addImage(
              logoBase64,
              "PNG",
              pageWidth - margin - logoWidth,
              yPosition,
              logoWidth,
              logoHeight
            );
          } catch (error) {
            console.warn("Could not add page logo:", error);
          }
        }

        // Only add section title if it's not empty
        const sectionTitle = (section.title || section.name || "")
          .replace(/^\*\*|\*\*$/g, '') // Remove ** markers
          .replace(/^\*|\*$/g, '') // Remove * markers
          .trim();

        if (sectionTitle) {
          doc.setFontSize(18);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
          doc.text(sectionTitle, margin, yPosition);

          // Add underline
          yPosition += 5;
          doc.setDrawColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
          doc.setLineWidth(0.5);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);

          yPosition += 15;
        }

        // Process section content with proper formatting
        const content = section.content || section.description || "No content available.";
        console.log(`📄 Section content length: ${content.length} characters`);

        yPosition = processContentWithFormatting(
          doc,
          content,
          margin,
          yPosition,
          pageWidth,
          pageHeight,
          logoBase64,
          { primaryRgb, secondaryRgb, textRgb }
        );
      });
    }

    // Save the PDF
    doc.save(filename);

    console.log("EMERGENCY PDF generated successfully!");
    return true;
  } catch (error) {
    console.error("EMERGENCY PDF generation failed:", error);
    throw error;
  }
};

// Helper function to process content with proper formatting
const processContentWithFormatting = (
  doc: any,
  content: string,
  margin: number,
  startY: number,
  pageWidth: number,
  pageHeight: number,
  logoBase64?: string | null,
  themeConfig?: { primaryRgb: any, secondaryRgb: any, textRgb: any }
): number => {
  let yPosition = startY;
  const colors = themeConfig || {
    primaryRgb: { r: 29, g: 78, b: 216 },
    secondaryRgb: { r: 55, g: 65, b: 81 },
    textRgb: { r: 0, g: 0, b: 0 }
  };

  console.log("🔄 Processing content formatting...");

  // Split content into paragraphs
  const paragraphs = content.split(/\n\s*\n/);

  paragraphs.forEach((paragraph, paragraphIndex) => {
    if (!paragraph.trim()) return;

    console.log(`📝 Processing paragraph ${paragraphIndex + 1}: ${paragraph.substring(0, 50)}...`);

    // Check if it's a heading
    if (paragraph.match(/^#{1,3}\s/)) {
      const level = (paragraph.match(/^#+/) || [""])[0].length;
      const headingText = paragraph
        .replace(/^#+\s*/, "")
        .replace(/^\*\*|\*\*$/g, '') // Remove ** markers
        .replace(/^\*|\*$/g, '') // Remove * markers
        .trim();

      // Add space before heading
      yPosition += level === 1 ? 15 : 10;

      // Set heading style with hierarchical colors
      doc.setFontSize(level === 1 ? 16 : level === 2 ? 14 : 13);
      doc.setFont("helvetica", "bold");
      const headingRgb = level === 1 ? colors.primaryRgb : colors.secondaryRgb;
      doc.setTextColor(headingRgb.r, headingRgb.g, headingRgb.b); // blue color for headings removed

      // Check for page break
      if (yPosition > pageHeight - margin - 20) {
        doc.addPage();
        yPosition = margin + 10;

        // Add logo to new page
        if (logoBase64) {
          try {
            const logoWidth = 20;
            const logoHeight = 12;
            doc.addImage(
              logoBase64,
              "PNG",
              pageWidth - margin - logoWidth,
              yPosition,
              logoWidth,
              logoHeight
            );
          } catch (error) {
            console.warn("Could not add logo to new page:", error);
          }
        }
        yPosition += 15;
      }

      const headingLines = doc.splitTextToSize(
        headingText,
        pageWidth - 2 * margin
      );
      headingLines.forEach((line: string) => {
        doc.text(line, margin, yPosition);
        yPosition += level === 1 ? 8 : 7;
      });

      yPosition += 5; // Space after heading
    } else {
      // Regular paragraph - process for bold and italic with improved parsing
      yPosition += 8; // Space before paragraph

      // Enhanced markdown parsing
      const processTextWithMarkdown = (text: string) => {
        // Split by markdown patterns while preserving them
        const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/);

        parts.forEach((part) => {
          if (!part.trim()) return;

          let processedText = part;
          let isBold = false;
          let isItalic = false;

          // Check formatting and clean text
          if (part.match(/^\*\*[^*]+\*\*$/)) {
            processedText = part.replace(/^\*\*|\*\*$/g, "");
            isBold = true;
          } else if (part.match(/^\*[^*]+\*$/)) {
            processedText = part.replace(/^\*|\*$/g, "");
            isItalic = true;
          }

          // Set text style
          doc.setFontSize(12);
          doc.setFont(
            "helvetica",
            isBold ? "bold" : isItalic ? "italic" : "normal"
          );
          doc.setTextColor(colors.textRgb.r, colors.textRgb.g, colors.textRgb.b); // Black for regular text removed

          // Handle lists
          if (processedText.match(/^[\*\-]\s/)) {
            processedText = processedText.replace(/^[\*\-]\s/, "• ");
          }

          const textLines = doc.splitTextToSize(processedText, pageWidth - 2 * margin);

          textLines.forEach((line: string) => {
            // Check for page break
            if (yPosition > pageHeight - margin - 10) {
              doc.addPage();
              yPosition = margin + 10;

              // Add logo to new page
              if (logoBase64) {
                try {
                  const logoWidth = 20;
                  const logoHeight = 12;
                  doc.addImage(
                    logoBase64,
                    "PNG",
                    pageWidth - margin - logoWidth,
                    yPosition,
                    logoWidth,
                    logoHeight
                  );
                } catch (error) {
                  console.warn("Could not add logo to new page:", error);
                }
              }
              yPosition += 15;
            }

            if (line.trim()) {
              doc.text(line, margin, yPosition);
              yPosition += 6;
            }
          });
        });
      };

      processTextWithMarkdown(paragraph);
      yPosition += 5; // Space after paragraph
    }
  });

  console.log("✅ Content formatting completed");
  return yPosition;
};

// Helper function to generate the same HTML as preview page
const generatePreviewHTML = (documentData: any, documentTheme: any): string => {
  const sections = documentData.editableSections || documentData.sections || [];
  let html = "";
  const showTitlePage = false; // No title page - start directly with sections


  // Content pages
  sections.forEach((section: any, index: number) => {
    const blocks =
      section.blocks || convertContentToBlocks(section.content || "");
    const renderedContent = renderBlocksToHTML(blocks);

    html += `
      <div class="${index === 0 ? "" : "page-break-before"
      }" style="
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        background: white;
        position: relative;
      ">
        <!-- Page Header -->
        <div style="
          background: linear-gradient(to right, #2563eb, #1d4ed8);
          color: white;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        ">
          <div style="display: flex; align-items: center; gap: 1rem;">
            ${documentTheme?.logo
        ? `
              <div style="background: white; padding: 0.375rem; border-radius: 0.25rem;">
                <img src="${documentTheme.logo}" alt="Company Logo" style="height: 2rem; object-fit: contain;" />
              </div>
            `
        : ""
      }
            <div>
              <h2 style="margin: 0; font-size: 1.125rem; font-weight: 600;">${section.title || section.name || `Section ${index + 1}`
      }</h2>
            </div>
          </div>
          <div>
            <p style="margin: 0; font-size: 0.875rem; opacity: 0.8;">Page ${showTitlePage ? index + 2 : index + 1
      }</p>
          </div>
        </div>

        <!-- Page Content -->
        <div style="
          flex: 1;
          padding: 2rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.7;
          font-size: 16px;
          color: #374151;
        ">
          ${renderedContent}
        </div>

        <!-- Page Footer -->
        <div style="
          background: #111827;
          color: white;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: auto;
        ">
          <div style="display: flex; align-items: center; gap: 1rem;">
            ${documentTheme?.logo
        ? `
              <div style="background: white; padding: 0.25rem; border-radius: 0.25rem; opacity: 0.9;">
                <img src="${documentTheme.logo}" alt="Logo" style="height: 1.25rem; object-fit: contain;" />
              </div>
            `
        : ""
      }
            <div>
              <p style="margin: 0; font-size: 0.75rem; opacity: 0.6;">${documentData.title || "Document"
      }</p>
            </div>
          </div>
          <div>
            <p style="margin: 0; font-size: 0.75rem; opacity: 0.6;">${new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    `;
  });

  return html;
};

// Simplified HTML generation that works better with html2pdf
const generateSimplifiedHTML = (
  documentData: any,
  documentTheme: any
): string => {
  const sections = documentData.editableSections || documentData.sections || [];
  const showTitlePage = false; // No title page - start directly with sections
  let html = `
    <div style="font-family: 'Inter', Arial, sans-serif; color: #333; line-height: 1.6;">
  `;


  // Content pages (simplified)
  sections.forEach((section: any, index: number) => {
    const blocks =
      section.blocks || convertContentToBlocks(section.content || "");
    const renderedContent = renderBlocksToHTML(blocks);

    html += `
      <div class="${index === 0 ? "" : "page-break-before"
      }" style="
        min-height: 100vh;
        padding: 2rem;
        background: white;
        ${index > 0 || showTitlePage ? "page-break-before: always;" : ""}
      ">
        <!-- Page Header -->
        <div style="
          border-bottom: 3px solid #1d4ed8;
          padding-bottom: 1rem;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        ">
          <div style="display: flex; align-items: center; gap: 1rem;">
            ${documentTheme?.logo
        ? `
              <img src="${documentTheme.logo}" alt="Logo" style="height: 30px; object-fit: contain;" />
            `
        : ""
      }
            <h2 style="
              margin: 0;
              font-size: 1.5rem;
              font-weight: 600;
              color: #1d4ed8;
            ">${section.title || section.name || `Section ${index + 1}`}</h2>
          </div>
          <div style="font-size: 0.9rem; color: #666;">
            Page ${showTitlePage ? index + 2 : index + 1}
          </div>
        </div>

        <!-- Page Content -->
        <div style="
          font-size: 14px;
          line-height: 1.7;
          color: #374151;
        ">
          ${renderedContent}
        </div>

        <!-- Page Footer -->
        <div style="
          margin-top: 3rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.8rem;
          color: #666;
        ">
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            ${documentTheme?.logo
        ? `
              <img src="${documentTheme.logo}" alt="Logo" style="height: 16px; object-fit: contain; opacity: 0.7;" />
            `
        : ""
      }
            <span>${documentData.title || "Document"}</span>
          </div>
          <div>
            ${new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    `;
  });

  html += `</div>`;

  return html;
};

// Simple HTML generation that definitely works with html2pdf
const generateBasicHTML = (documentData: any, documentTheme: any): string => {
  const sections = documentData.editableSections || documentData.sections || [];

  let html = `
    <div style="font-family: Arial, sans-serif; color: #333; line-height: 1.5;">
      <!-- Title Page -->
      <div style="text-align: center; padding: 100px 20px; page-break-after: always;">
        ${documentTheme?.logo
      ? `
          <div style="margin-bottom: 40px;">
            <img src="${documentTheme.logo}" alt="Logo" style="max-width: 200px; max-height: 100px;" />
          </div>
        `
      : ""
    }

        <h1 style="font-size: 32px; color: #1d4ed8; margin: 30px 0;">${documentData.title || "Document Title"
    }</h1>

        ${documentData.client
      ? `
          <p style="font-size: 20px; margin: 20px 0;">Prepared for: <strong>${documentData.client}</strong></p>
        `
      : ""
    }

        ${documentData.type
      ? `
          <p style="font-size: 16px; margin: 20px 0; font-style: italic;">${documentData.type}</p>
        `
      : ""
    }

        <p style="font-size: 14px; margin-top: 60px; color: #666;">
          ${new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}
        </p>
      </div>
  `;

  // Content sections
  sections.forEach((section: any, index: number) => {
    const content = section.content || "";
    const processedContent = processContentForPDF(content);

    html += `
      <div style="padding: 30px 20px; ${index > 0 ? "page-break-before: always;" : ""
      }">
        <!-- Section Header -->
        <div style="border-bottom: 3px solid #1d4ed8; padding-bottom: 15px; margin-bottom: 25px;">
          <h2 style="color: #1d4ed8; font-size: 24px; margin: 0;">
            ${section.title || section.name || `Section ${index + 1}`}
          </h2>
        </div>

        <!-- Section Content -->
        <div style="font-size: 14px; line-height: 1.6;">
          ${processedContent}
        </div>
      </div>
    `;
  });

  html += `</div>`;
  return html;
};

// Process content for PDF (convert markdown to HTML)
const processContentForPDF = (content: string): string => {
  if (!content) return "<p>No content available.</p>";

  return (
    content
      // Convert headings
      .replace(
        /^### (.*$)/gim,
        '<h3 style="color: #1d4ed8; font-size: 18px; margin: 20px 0 10px 0;">$1</h3>'
      )
      .replace(
        /^## (.*$)/gim,
        '<h2 style="color: #1d4ed8; font-size: 20px; margin: 25px 0 15px 0;">$1</h2>'
      )
      .replace(
        /^# (.*$)/gim,
        '<h1 style="color: #1d4ed8; font-size: 22px; margin: 30px 0 20px 0;">$1</h1>'
      )
      // Convert bold and italic
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      // Convert lists
      .replace(/^[\*\-] (.+)$/gim, '<li style="margin: 5px 0;">$1</li>')
      // Convert code
      .replace(
        /`(.*?)`/g,
        '<code style="background: #f5f5f5; padding: 2px 4px; border-radius: 3px;">$1</code>'
      )
      // Convert line breaks
      .replace(/\n\n/g, '</p><p style="margin: 15px 0;">')
      .replace(/\n/g, "<br>")
      // Wrap in paragraph
      .replace(/^(.*)$/, '<p style="margin: 15px 0;">$1</p>')
      // Fix list wrapping
      .replace(
        /<p[^>]*>(<li.*<\/li>)<\/p>/g,
        '<ul style="margin: 15px 0; padding-left: 25px;">$1</ul>'
      )
      .replace(/<\/li><br><li/g, "</li><li")
      // Convert blockquotes
      .replace(/^>\s?(.*)$/gm, "<blockquote style='border-left: 4px solid #3182ce; margin: 1.5rem 0; padding: 0.5rem 1rem; color: #888; font-style: italic; background: #f8fafc;'>$1</blockquote>")
  );
};

// Enhanced content-based PDF generation with professional formatting
export const generatePDFFromContent = async (
  documentData: any,
  documentTheme: any,
  filename: string
) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;

  // Enhanced theme colors with hierarchical system
  const primaryColor = documentTheme?.headingColor || "#1a365d"; // H1 headings
  const secondaryColor =
    documentTheme?.subheadingColor || documentTheme?.textColor || "#374151"; // H2, H3 headings
  const textColor = documentTheme?.textColor || "#2d3748"; // Body text
  const accentColor = documentTheme?.accentColor || "#3182ce"; // Accents and highlights
  const fontFamily =
    documentTheme?.fontFamily || "Inter, system-ui, -apple-system, sans-serif";

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
      : { r: 0, g: 0, b: 0 };
  };




  // Add content pages directly (no cover page)
  const sections = documentData.editableSections || documentData.sections || [];

  if (sections.length > 0) {
    let yPosition = margin + 10;
    let currentPageNum = 1; // Start from page 1 (no cover)

    // Add initial header
    const addPageHeader = (pageNum: number) => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(documentData.title || "Document", margin, 15);
      doc.text(`Page ${pageNum}`, pageWidth - margin, 15, { align: "right" });

      // Add separator line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(margin, 20, pageWidth - margin, 20);
    };

    addPageHeader(currentPageNum);
    yPosition = 35;

    for (const section of sections) {
      // Convert markdown content to HTML
      const htmlContent = convertMarkdownToHTML(section.content || "");

      // Create a temporary div to render HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.width = `${contentWidth}mm`;
      tempDiv.style.fontFamily = "helvetica";

      // Add to document
      const sectionHeight = tempDiv.offsetHeight;

      // Check if we need a new page
      if (yPosition + sectionHeight > pageHeight - margin) {
        doc.addPage();
        currentPageNum++;
        addPageHeader(currentPageNum);
        yPosition = 35;
      }

      // Add section title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      const titleRgb = hexToRgb(primaryColor);
      doc.setTextColor(titleRgb.r, titleRgb.g, titleRgb.b);
      doc.text(section.title || "", margin, yPosition);
      yPosition += 10;

      // Add section content
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const textRgb = hexToRgb(textColor);
      doc.setTextColor(textRgb.r, textRgb.g, textRgb.b);

      // Split content into lines that fit the page width
      const lines = doc.splitTextToSize(
        tempDiv.textContent || "",
        contentWidth
      );

      // Add lines to document
      for (const line of lines) {
        // Check if we need a new page
        if (yPosition > pageHeight - margin) {
          doc.addPage();
          currentPageNum++;
          addPageHeader(currentPageNum);
          yPosition = 35;
        }

        doc.text(line, margin, yPosition);
        yPosition += 7; // Line height
      }

      yPosition += 20; // Space between sections
    }
  }

  // Save the document
  doc.save(filename);
};
